import { AgxFormCheckbox, AgxFormTextField } from '@urbanx/agx.forms';
import { AgxButton, AgxSpacer, AgxTab, AgxTypography } from '@urbanx/agx.ui-components2';
import { SupportApiException, useStartIdentityMigration } from '@urbanx/agx.support.client';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Panel from 'components/panel/Panel';
import SupportBackButton from '../SupportBackButton';
import IdentityMigrationHistory from './IdentityMigrationHistory';
import { IdentityMigrationForm as Form } from './identityMigrationFormDefinition';

function StartIdentityMigration() {
  const navigate = useNavigate();
  const { startIdentityMigration, startIdentityMigrationPending } = useStartIdentityMigration();
  const [error, setError] = useState('');
  const form = Form.useForm({
    defaultValues: { sourceEmail: '', destinationEmail: '', reason: '', confirmed: false },
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      setError('');
      try {
        const migration = await startIdentityMigration(parsedValues);
        if (!migration)
          throw new Error('No migration was returned. Check the history tab before trying again.');
        navigate(migration.operationId);
        return { status: 'accepted', baseline: parsedValues };
      } catch (failure) {
        setError(
          failure instanceof SupportApiException &&
            (failure.status === 401 || failure.status === 403)
            ? 'Only Staff SuperAdmins can migrate identities.'
            : typeof failure === 'string'
              ? failure
              : failure instanceof Error
                ? failure.message
                : 'Unable to start migration.'
        );
        return { status: 'failed', error: failure };
      }
    },
  });

  return (
    <AgxSpacer gap="spacing-lg" fill>
      <AgxTypography id="identity-migration-description" type="body" size="medium">
        Move a person to a new login email. All memberships move together and existing access
        settings are preserved. The new external login must be arranged separately.
      </AgxTypography>
      <Form.Root form={form}>
        <AgxSpacer gap="spacing-lg" fill>
          <Form.Field
            name="sourceEmail"
            Input={AgxFormTextField}
            label="Current login email"
            type="text"
            stretch
          />
          <Form.Field
            name="destinationEmail"
            Input={AgxFormTextField}
            label="New login email"
            type="text"
            stretch
          />
          <Form.Field
            name="reason"
            Input={AgxFormTextField}
            label="Reason or ticket reference"
            type="text"
            stretch
          />
          <Form.Field
            name="confirmed"
            Input={AgxFormCheckbox}
            label="I confirm this is the same person receiving a new login"
          />
          <Form.Submit>
            {({ submit, canSubmit }) => (
              <AgxButton
                id="identity-migration-start"
                variant="primary"
                text={startIdentityMigrationPending ? 'Starting migration…' : 'Start migration'}
                disabled={!canSubmit || startIdentityMigrationPending}
                onClick={() => void submit()}
              />
            )}
          </Form.Submit>
        </AgxSpacer>
      </Form.Root>
      {error && (
        <AgxTypography type="form_error" role="alert">
          {error}
        </AgxTypography>
      )}
    </AgxSpacer>
  );
}

export default function IdentityMigration() {
  const [searchParams, setSearchParams] = useSearchParams();
  const history = searchParams.get('tab') === 'history';

  return (
    <AgxSpacer gap="spacing-lg" fill>
      <SupportBackButton />
      <Panel title="Identity migration">
        <AgxSpacer gap="spacing-lg" fill>
          <AgxTab
            options={[
              { id: 'start', label: 'Start migration', selected: !history },
              { id: 'history', label: 'History', selected: history },
            ]}
            onChange={(tab) => setSearchParams(tab.id === 'history' ? { tab: 'history' } : {})}
          />
          {history ? <IdentityMigrationHistory /> : <StartIdentityMigration />}
        </AgxSpacer>
      </Panel>
    </AgxSpacer>
  );
}
